import { fetchFromContentful } from "../utils/contentful/api";
import gql from "graphql-tag";
import { renderContentfulRichText } from "../components/Contentful/RichText";

import { Link } from "../components/Actions";

import { VideoGroup, VideoScroller } from "../components/VideoGroup";

import { formatDate } from "../utils/dateTime";
import SEO, { deriveDataFromContentfulEntry } from "../components/Seo/Seo";
import { PreviewWrapper } from "../components/PreviewWrapper/PreviewWrapper";
import TokenGatedVideo from "../components/TokenGatedVideo/TokenGatedVideo";
import { useAccount } from "wagmi";
import { useIsHolder } from "../hooks/isHolder";
import { ShareButtons } from "@/components/ShareButtons";

const WatchPage = ({ post, preview, videos, fullSlug }: any) => {
  const { isHolder } = useIsHolder();
  const { address } = useAccount();

  const displayDate = formatDate(post.publishedDate);
  const hasVideo = post.video;

  return (
    <PreviewWrapper preview={preview}>
      <SEO {...deriveDataFromContentfulEntry(post.socialShareDate)} />

      <article className="mb-[30px] tab:mb-0 w-[calc(100%-40px)] mx-auto overflow-hidden">
        <div className="max-w-[800px] mx-auto pt-[60px]">
          <h1 className="display3 text-coinage-blue mb-2">
            {post.articleTitle}
          </h1>
          <p className="body1 text-coinage-body mb-10">
            {post.articleDescription}
          </p>
          <div className="flex flex-col md:flex-row items-top justify-between gap-4">
            <div>
              {post.byline && (
                <p className="body1 text-coinage-black mb-4 !font-bold">
                  {post.byline}
                </p>
              )}
              <p className="body1 text-coinage-body mb-4">{displayDate}</p>
            </div>
            <div>
              <ShareButtons href={fullSlug} title={post.articleTitle} />
            </div>
          </div>
          <hr className="my-8 border-coinage-stroke" />
          {hasVideo && (
            <TokenGatedVideo
              video={post?.video}
              isHolder={isHolder}
              address={address}
            />
          )}
        </div>

        <div className="py-8 tab:pb-[100px] max-w-[800px] mx-auto">
          {renderContentfulRichText(post.articleContent)}
        </div>

        <div className="flex flex-wrap items-center justify-between content-wrapper pb-6 tab:pb-0">
          <h2 className="display2 mb-2">MORE EPISODES</h2>
          <Link
            className="hidden tab:inline-flex"
            analyticsProperty="view all"
            modifier="primary"
            href="/watch">
            View All
          </Link>
        </div>
        <div className="w-full p-0 article-page-mobile-carousel flex items-center flex-col">
          <VideoGroup
            showDescription={true}
            showBorderTop={false}
            title=""
            videoClasses="w-[90%]"
            videos={videos}
            sizes={{ max: 245, min: 180 }}
            maxVideos={{ desktop: 4, mobile: 4 }}
          />
          <Link
            className="tab:hidden mb-4"
            analyticsProperty="view all"
            modifier="primary"
            href="/watch">
            View All
          </Link>
        </div>
      </article>
    </PreviewWrapper>
  );
};

export const getStaticPaths = async () => {
  const query = gql`
    query {
      videoArticleCollection {
        items {
          urlSlug
          seasonGroup {
            slug
          }
        }
      }
    }
  `;

  const { data } = await fetchFromContentful({ query });

  const paths = data.videoArticleCollection.items.map(
    ({ urlSlug, seasonGroup }: any) => ({
      params: {
        slug: [`${seasonGroup.slug}/${urlSlug}`]
      }
    })
  );

  return {
    paths,
    fallback: "blocking"
  };
};

export const getStaticProps = async ({ params, preview = false }: any) => {
  const slugParts = params.slug;
  const slug = slugParts[slugParts.length - 1];

  const articleQuery = gql`
    query SingleArticle($slug: String, $preview: Boolean) {
      vids: videoArticleCollection(
        limit: 4
        preview: $preview
        order: publishedDate_DESC
        where: { urlSlug_not: $slug }
      ) {
        items {
          seasonGroup {
            slug
          }
          category {
            categoryTitle
          }
          urlSlug
          articleTitle
          articleDescription
          shortArticleDescription
          video {
            videoUpload
            fallbackImage {
              url
              width
              height
              description
            }
          }
          image {
            url
            width
            height
            description
          }
        }
      }
      videoArticleCollection(
        where: { urlSlug: $slug }
        limit: 1
        preview: $preview
      ) {
        items {
          socialShareDate {
            title
            excerpt
            shareImage {
              url
            }
          }
          urlSlug
          articleTitle
          articleDescription
          episodeNumber
          seasonGroup {
            seasonNumber
          }
          byline
          publishedDate
          articleContent {
            json
            links {
              entries {
                block {
                  ... on CodeEmbed {
                    code
                    __typename
                  }
                  sys {
                    id
                  }
                }
              }
              assets {
                block {
                  description
                  width
                  height
                  url
                  sys {
                    id
                  }
                }
              }
            }
          }
          image {
            url
            width
            height
            description
          }
          video {
            videoUpload
            videoTitle
            fallbackImage {
              description
              url
              width
              height
            }
            sys {
              id
            }
          }
        }
      }
    }
  `;

  const { data, errors } = await fetchFromContentful({
    query: articleQuery,
    variables: { slug },
    preview
  });

  if (errors) {
    console.error(errors);
  }

  const post = data.videoArticleCollection.items[0];
  const vids = data.vids.items;
  if (!post) {
    return {
      notFound: true,
      revalidate: 300
    };
  }

  return {
    props: { post, preview, videos: vids, fullSlug: params.slug.join("/") },
    revalidate: 300
  };
};

export default WatchPage;
